import { BaseService, ApiError } from './core/BaseService';
import { Model } from '../types';
import { useAuthStore } from '../stores/authStore';

export interface GenerateRequest {
    modelId?: string;
    prompt?: string;
    mode?: string;
    inputImage?: string;
    inputImages?: string[];
    // v1.4.97 参考图独立通道（后端 GenerateRequest.referenceImages 已预留同名字段）：
    // 视频/图像生成的"参考语义"图片与 inputImages（首帧/待处理图等"输入语义"）分离，
    // 现有适配器不消费此通道（零行为影响），img_ref 语义适配器实现后直接读取
    referenceImages?: string[];
    params?: Record<string, any>;
    projectId?: string;
    nodeId?: string;
    feature?: string;
}

export interface GenerateResult {
    taskId: string;
    status: string;
    results?: Array<{ url: string; outputType: string; base64?: string }>;
    error?: string;
    videoMetadata?: Record<string, any>;
    analysis?: string;
    // LLM 结构化产物通道（脚本节点等 feature 分支回传非媒体数据，如 { shots, entities }）
    data?: Record<string, any>;
    // 积分系统 P1：扣费后总余额（points + daily_points），前端据此即时刷新余额展示
    creditsBalance?: number;
}

export interface TaskStatusResult {
    taskId: string;
    // 🔴 与后端 P1-38 任务状态机对齐（原类型漏了 queued，导致 queued 被当作
    // unknown 兜底误判为 FAILED）
    status: 'pending' | 'queued' | 'running' | 'success' | 'failed' | 'cancelled';
    result?: any;
    error?: string;
    traceback?: string;
}

export interface ProjectTaskItem {
    taskId: string;
    status: 'pending' | 'queued' | 'running' | 'success' | 'failed' | 'cancelled';
    request?: Record<string, any>;
    nodeId?: string | null;
    projectId?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface ProjectTasksResult {
    projectId: string;
    tasks: ProjectTaskItem[];
    count: number;
}

class GenerateApiService extends BaseService {
    async getModels(): Promise<Model[]> {
        const data = await this.get('/api/models');
        return (data as any).models || [];
    }

    /** 生成即预扣：提交响应后刷新 authStore 积分分池快照，顶栏/明细页余额与流水一致。
     *  集中挂载于此而非 12+ 个调用点，sync/async 两条通道（含未来新增）自动覆盖。 */
    private syncCreditsBalance(): void {
        void useAuthStore.getState().refreshPoints();
    }

    async generateSync(request: GenerateRequest): Promise<GenerateResult> {
        const res = await this.post<GenerateResult>('/api/generate/sync', request);
        this.syncCreditsBalance();
        return res;
    }

    async generateAsync(request: GenerateRequest): Promise<{ taskId: string; status: string; message: string; image?: string; videoUri?: string; audioUri?: string; analysis?: string; creditsBalance?: number }> {
        const res = await this.post<{ taskId: string; status: string; message: string; image?: string; videoUri?: string; audioUri?: string; analysis?: string; creditsBalance?: number }>('/api/generate', request);
        this.syncCreditsBalance();
        return res;
    }

    async getTaskStatus(taskId: string): Promise<TaskStatusResult> {
        return this.get<TaskStatusResult>(`/api/task/${taskId}/status`);
    }

    async getProjectTasks(projectId: string): Promise<ProjectTasksResult> {
        return this.get<ProjectTasksResult>(`/api/projects/${projectId}/tasks`);
    }

    async cancelTask(taskId: string): Promise<void> {
        await this.post(`/api/cancel/${taskId}`);
    }

    async pollTaskUntilComplete(
        taskId: string,
        onProgress?: (status: TaskStatusResult) => void,
        maxAttempts: number = 360,
        interval: number = 5000,
    ): Promise<TaskStatusResult> {
        // v1.4.33 瞬态故障容忍：后端重启/抖动窗口内轮询 5xx 不再立即炸出循环判死生成。
        // 事故背景：任务在后端已 success（结果完整落库），但轮询恰逢后端不可用窗口，
        // 连续 500 导致前端把已成功的任务标记为"生成失败 internal_error"，成果被永久丢弃。
        // 任务终态以后端 DB 记录为准，短暂容忍 + 继续轮询即可在后端恢复后原样捞回结果。
        const MAX_CONSECUTIVE_ERRORS = 5;
        const TRANSIENT_ERROR_WAIT_MS = Math.min(interval, 3000);
        // v1.4.50 判死前终态复查：连续错误达到阈值时不立即判死，先等待一个恢复窗口再查一次——
        // 事故背景：脚本 LLM 任务执行期间后端出现约几十秒的瞬时故障窗口（/api/task status 连续 500），
        // 轮询 5 连败提前判死报「脚本生成失败」，而任务随后在 16:10:48 success 完整落库，成果被误弃。
        // 复查拿到终态则原样捞回；后端恢复但任务未完成则清零计数继续轮询；复查仍失败才按原错误判死
        const FINAL_RECHECK_WAIT_MS = 15000;
        let consecutiveErrors = 0;

        for (let i = 0; i < maxAttempts; i++) {
            let status: TaskStatusResult;
            try {
                status = await this.getTaskStatus(taskId);
                consecutiveErrors = 0;
            } catch (e: any) {
                // 404：任务记录不存在（真丢失），快速失败，不浪费轮询预算
                if (e instanceof ApiError && e.status === 404) throw e;
                consecutiveErrors++;
                if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                    await new Promise(r => setTimeout(r, FINAL_RECHECK_WAIT_MS));
                    try {
                        const recheck = await this.getTaskStatus(taskId);
                        if (
                            recheck.status === 'success' ||
                            recheck.status === 'failed' ||
                            recheck.status === 'cancelled'
                        ) {
                            return recheck;
                        }
                        consecutiveErrors = 0;
                        continue;
                    } catch {
                        throw e;
                    }
                }
                await new Promise(r => setTimeout(r, TRANSIENT_ERROR_WAIT_MS));
                continue;
            }

            onProgress?.(status);

            if (status.status === 'success' || status.status === 'failed' || status.status === 'cancelled') {
                return status;
            }

            await new Promise(r => setTimeout(r, interval));
        }

        throw new Error('任务轮询超时');
    }
}

export const generateApi = new GenerateApiService();

/** 402 积分不足错误详情（后端 HTTPException detail: {message, need, balance}，完整 detail 存于 ApiError.body.detail） */
export interface CreditsErrorDetail {
  message: string;
  need: number;
  balance: number;
}

/**
 * 从生成类接口异常中提取积分不足详情（积分系统开发方案 v1.10 七节 402 拦截）：
 * 仅 402 且 detail 为结构化对象时返回，其余错误（网络/401/5xx）返回 null 由调用方按原逻辑处理。
 * 供批量生成循环中断判断与统一「积分不足」提示消费。
 */
export function getCreditsErrorDetail(e: unknown): CreditsErrorDetail | null {
  if (e instanceof ApiError && e.status === 402) {
    const detail = (e.body as any)?.detail;
    if (detail && typeof detail === 'object') {
      return {
        message: typeof detail.message === 'string' && detail.message ? detail.message : '积分不足',
        need: Number(detail.need) || 0,
        balance: Number(detail.balance) || 0,
      };
    }
  }
  return null;
}
